<template>
  <div class="space-y-8">
    <!-- Hero Section -->
    <section class="text-center py-12 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900 dark:to-accent-900 rounded-2xl">
      <div class="max-w-3xl mx-auto px-4">
        <h1 class="text-4xl font-bold text-gray-900 dark:text-gray-50 mb-4">Welcome to Purdia Docs</h1>
        <p class="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
          Comprehensive documentation for the Purdia Trading Platform development team. Access project overviews, coding standards, API documentation, and deployment guides all in one place.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <router-link to="/docs/app-overview" class="btn-primary">
            Get Started
          </router-link>
          <router-link to="/docs/standards" class="btn-secondary">
            View Standards
          </router-link>
        </div>
      </div>
    </section>

    <!-- Quick Stats Section -->
    <section class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8">
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-4">📊 Quick Overview</h2>
        <p class="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Your documentation is organized into logical categories for easy navigation.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div class="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Documentation</h3>
          <p class="text-gray-600 dark:text-gray-300 mb-4">
            {{ documentation.length }} categories available
          </p>
        </div>

        <div class="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Status</h3>
          <p class="text-gray-600 dark:text-gray-300 mb-4">
            {{ storeLoaded ? 'Loaded' : 'Loading...' }}
          </p>
        </div>

        <div class="text-center p-6 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Last Updated</h3>
          <p class="text-gray-600 dark:text-gray-300 mb-4">
            {{ mountTime }}
          </p>
        </div>
      </div>
    </section>

    <!-- Categories Preview -->
    <section class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8">
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-4">📚 Documentation Categories</h2>
        <p class="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Browse through organized documentation by category.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="category in documentation.slice(0, 6)" 
          :key="category.id"
          class="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600 transition-colors duration-200"
        >
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">{{ category.title }}</h3>
          <p class="text-gray-600 dark:text-gray-300 mb-4 text-sm">
            {{ category.description }}
          </p>
          <div class="flex justify-between items-center">
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ category.sections.length }} sections
            </span>
            <router-link 
              :to="`/docs/${category.slug}`"
              class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
            >
              View →
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useDocumentationStore } from '@/stores/documentation'

  // Store
  const documentationStore = useDocumentationStore()

  // Refs
  const mountTime = ref(new Date().toLocaleTimeString())

  // Computed
  const documentation = computed(() => documentationStore.documentation)
  const storeLoaded = computed(() => documentationStore.documentation.length > 0)



  // Lifecycle
  onMounted(() => {
    // Load documentation from markdown if none exists
    if (documentation.value.length === 0) {
      documentationStore.loadDocumentationFromMarkdown()
    }
  })
</script>

<style scoped>
.btn-primary {
  @apply px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium;
}

.btn-secondary {
  @apply px-6 py-3 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200 font-medium;
}
</style>